import type { CreateVenueRequest, UpdateVenueRequest, UserDto } from '@pokerroom/types';
/**
 * @component VenueManagementPage
 * @description 場館管理 — 列表、建立、編輯場館 + 跨場館搜尋 + 報表 (最高管理員 only)
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import VenuePreparationPanel from './VenuePreparationPanel';
import {
  groupEnrollmentOutcomes,
  normalizeSelection,
  type EnrollmentOutcome,
} from '../venue-onboarding-ui';

interface Venue {
  id: string;
  name: string;
  code: string;
  edition: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface VenueDetail extends Venue {
  _count: {
    userVenues: number;
    tournaments: number;
    memberVenues: number;
    venueTables: number;
  };
}

interface MemberVenueInfo {
  venueId: string;
  memberNo: string;
  status: string;
  rewardPoints: number;
  totalDebt: string;
  level: { code: string; displayName: string; color: string } | null;
  venue: { name: string; code: string };
}

interface GlobalMember {
  id: string;
  nickname: string;
  phone: string | null;
  email: string | null;
  photoUrl: string | null;
  createdAt: string;
  memberVenues: MemberVenueInfo[];
}

interface VenueStat {
  venueId: string;
  venueName: string;
  venueCode: string;
  tournamentCount: number;
  totalPrizePool: number;
  totalPlayers: number;
  avgBuyin: number;
}

interface TournamentReport {
  venues: VenueStat[];
  grandTotal: {
    tournamentCount: number;
    totalPrizePool: number;
    totalPlayers: number;
  };
}

type Tab = 'venues' | 'members' | 'reports';

/**
 * Single escaped hyphen (not a bare trailing `-`) so the source is unambiguous
 * regardless of position inside the character class, in both the JS check and
 * the native HTML `pattern` attribute.
 */
const VENUE_CODE_PATTERN = '[A-Za-z0-9_\\-]{1,32}';
const VENUE_CODE_REGEX = /^[A-Za-z0-9_-]{1,32}$/;

/** Prefers the backend business message; never renders a raw Error/object/array. */
function extractBusinessErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: unknown } } }).response;
    const message = response?.data?.message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return fallback;
}

export default function VenueManagementPage() {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const preparationId = searchParams.get('onboarding');
  const revision = useAuthStore((s) => s.authRevision);
  const [tab, setTab] = useState<Tab>('venues');
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateVenueRequest>({
    name: '',
    code: '',
    edition: 'BUSINESS',
    initialAdministratorId: '',
  });
  const openPreparation = (id: string | null) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (id) next.set('onboarding', id);
      else next.delete('onboarding');
      return next;
    });
  };

  // Member search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Report state
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');

  const [codeDrafts, setCodeDrafts] = useState<Record<string, string>>({});
  const [renameDrafts, setRenameDrafts] = useState<Record<string, string>>({});
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [targetVenueId, setTargetVenueId] = useState('');
  const [enrollmentOutcomes, setEnrollmentOutcomes] = useState<EnrollmentOutcome[]>([]);

  // ---- Queries ----
  const { data: venues = [], isLoading } = useQuery<Venue[]>({
    queryKey: ['venues'],
    queryFn: () => api.get<Venue[]>('/venues'),
  });

  const administrators = useQuery({
    queryKey: ['venue-administrator-options', revision],
    queryFn: ({ signal }) => api.get<UserDto[]>('/users', { signal }),
    enabled: showCreate,
    retry: false,
  });

  const { data: detail } = useQuery<VenueDetail>({
    queryKey: ['venues', editingId],
    queryFn: () => api.get<VenueDetail>(`/venues/${editingId}`),
    enabled: !!editingId,
  });

  const { data: searchResults = [], isFetching: isSearching } = useQuery<GlobalMember[]>({
    queryKey: ['venues', 'members', 'search', searchQuery],
    queryFn: () =>
      api.get<GlobalMember[]>(
        `/venues/members/search?q=${encodeURIComponent(searchQuery)}&limit=30`,
      ),
    enabled: searchQuery.length >= 2,
  });

  const { data: report, isFetching: isReportLoading } = useQuery<TournamentReport>({
    queryKey: ['venues', 'reports', 'tournaments', reportFrom, reportTo],
    queryFn: () => {
      const params = new URLSearchParams();
      if (reportFrom) params.set('from', reportFrom);
      if (reportTo) params.set('to', reportTo);
      return api.get<TournamentReport>(`/venues/reports/tournaments?${params}`);
    },
    enabled: tab === 'reports',
  });

  // ---- Mutations ----
  const createMutation = useMutation({
    mutationFn: (data: CreateVenueRequest) => api.post<Venue>('/venues', data),
    onSuccess: async (venue, data) => {
      if (data.initialAdministratorId === useAuthStore.getState().user?.id) {
        await queryClient.cancelQueries();
        queryClient.clear();
        useAuthStore.getState().clearAuth();
        window.location.assign(`/login?venueOnboarding=${encodeURIComponent(venue.id)}`);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      setShowCreate(false);
      setForm({ name: '', code: '', edition: 'BUSINESS', initialAdministratorId: '' });
      openPreparation(venue.id);
    },
    onError: () => void queryClient.invalidateQueries({ queryKey: ['venues'] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVenueRequest }) =>
      api.patch(`/venues/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      setEditingId(null);
    },
  });

  const codeMutation = useMutation({
    mutationFn: ({ id, code }: { id: string; code: string }) =>
      api.patch(`/venues/${id}`, { code } satisfies UpdateVenueRequest),
    onSuccess: async (_data, { id }) => {
      setCodeDrafts((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['venues'] }),
        queryClient.invalidateQueries({ queryKey: ['current-venue'] }),
      ]);
    },
  });

  const enrollmentMutation = useMutation({
    mutationFn: () =>
      api.post<{ outcomes: EnrollmentOutcome[] }>(`/venues/${targetVenueId}/members/enrollments`, {
        memberIds: normalizeSelection(selectedMemberIds),
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['venues', 'members', 'search'] });
      setEnrollmentOutcomes(result.outcomes);
      setSelectedMemberIds([]);
    },
  });

  // ---- Tab content renderers ----

  const renderVenuesTab = () => (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold">{t('venue.title')}</h2>
          <p className="text-sm text-base-content/50">{t('venue.subtitle')}</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">
          {t('venue.newVenue')}
        </button>
      </div>

      {preparationId && (
        <VenuePreparationPanel
          key={preparationId}
          venueId={preparationId}
          name={venues.find((v) => v.id === preparationId)?.name ?? preparationId}
          onClose={() => openPreparation(null)}
        />
      )}

      {showCreate && (
        <div className="card bg-base-200 mb-6">
          <div className="card-body p-4">
            <h3 className="font-semibold mb-3">{t('venue.createVenue')}</h3>
            <div className="grid grid-cols-3 gap-3">
              <input
                type="text"
                placeholder={t('venue.venueName')}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="input input-sm input-bordered"
              />
              <input
                type="text"
                placeholder={t('venue.codePlaceholder')}
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                maxLength={32}
                pattern={VENUE_CODE_PATTERN}
                required
                className="input input-sm input-bordered"
              />
              <select
                value={form.edition}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    edition: e.target.value as CreateVenueRequest['edition'],
                  }))
                }
                className="select select-sm select-bordered"
              >
                <option value="LITE">{t('venue.editionLite')}</option>
                <option value="BUSINESS">{t('venue.editionBusiness')}</option>
                <option value="ENTERPRISE">{t('venue.editionEnterprise')}</option>
              </select>
            </div>
            <label className="form-control mt-3">
              <span className="label-text mb-1">{t('venue.initialAdministrator')}</span>
              <select
                aria-label={t('venue.initialAdministrator')}
                value={form.initialAdministratorId}
                className="select select-sm select-bordered"
                disabled={administrators.isPending || administrators.isError}
                onChange={(e) => setForm((f) => ({ ...f, initialAdministratorId: e.target.value }))}
              >
                <option value="">{t('venue.chooseAdministrator')}</option>
                {administrators.data
                  ?.filter((u) => u.isActive)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.displayName} ({u.username})
                    </option>
                  ))}
              </select>
              <span className="text-sm mt-2">{t('venue.administratorGrantHint')}</span>
            </label>
            {administrators.isError && (
              <p role="alert" className="text-error text-sm">
                {t('venue.administratorsFailed')}
              </p>
            )}
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => createMutation.mutate(form)}
                disabled={
                  !form.name ||
                  !form.code ||
                  !form.initialAdministratorId ||
                  administrators.isError ||
                  !VENUE_CODE_REGEX.test(form.code) ||
                  createMutation.isPending
                }
                className="btn btn-primary btn-sm"
              >
                {createMutation.isPending ? t('venue.creating') : t('venue.create')}
              </button>
              <button onClick={() => setShowCreate(false)} className="btn btn-ghost btn-sm">
                {t('venue.cancel')}
              </button>
            </div>
            {createMutation.isError && (
              <p className="text-error text-sm mt-1">
                {extractBusinessErrorMessage(createMutation.error, t('venue.createFailed'))}
              </p>
            )}
            {createMutation.isError && venues.find((v) => v.code === form.code) && (
              <button
                className="btn btn-sm btn-outline mt-2"
                onClick={() => {
                  const existing = venues.find((v) => v.code === form.code);
                  if (existing) openPreparation(existing.id);
                }}
              >
                {t('venue.resumeExisting')}
              </button>
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <span className="loading loading-spinner" />
        </div>
      ) : (
        <div className="space-y-3">
          {venues.map((venue) => (
            <div
              key={venue.id}
              className={`card bg-base-200 ${!venue.isActive ? 'opacity-50' : ''}`}
            >
              <div className="card-body p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        {venue.name}
                        <span className="badge badge-sm badge-outline">{venue.code}</span>
                        <span
                          className={`badge badge-sm ${
                            venue.edition === 'ENTERPRISE'
                              ? 'badge-primary'
                              : venue.edition === 'BUSINESS'
                                ? 'badge-success'
                                : 'badge-ghost'
                          }`}
                        >
                          {venue.edition}
                        </span>
                        {!venue.isActive && (
                          <span className="badge badge-sm badge-error">{t('venue.inactive')}</span>
                        )}
                      </h3>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="btn btn-outline btn-xs"
                      onClick={() => openPreparation(venue.id)}
                    >
                      {t('venue.preparation')}
                    </button>
                    <button
                      disabled={codeMutation.isPending}
                      onClick={() => {
                        setEditingId(editingId === venue.id ? null : venue.id);
                        setCodeDrafts({});
                        codeMutation.reset();
                      }}
                      className="btn btn-ghost btn-xs"
                    >
                      {editingId === venue.id ? t('venue.close') : t('venue.detail')}
                    </button>
                  </div>
                </div>
                {editingId === venue.id && detail && (
                  <div className="mt-3 pt-3 border-t border-base-300">
                    <div className="grid grid-cols-4 gap-3 mb-3">
                      {[
                        { val: detail._count.userVenues, label: t('venue.users') },
                        { val: detail._count.tournaments, label: t('venue.tournaments') },
                        { val: detail._count.memberVenues, label: t('venue.members') },
                        { val: detail._count.venueTables, label: t('venue.tables') },
                      ].map(({ val, label }) => (
                        <div key={label} className="text-center">
                          <div className="text-lg font-bold">{val}</div>
                          <div className="text-xs text-base-content/50">{label}</div>
                        </div>
                      ))}
                    </div>
                    <form
                      className="mb-4 space-y-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        const code = codeDrafts[venue.id] ?? venue.code;
                        if (
                          codeMutation.isPending ||
                          code === venue.code ||
                          !VENUE_CODE_REGEX.test(code)
                        )
                          return;
                        if (
                          window.confirm(
                            t('venue.codeImpact', { oldCode: venue.code, newCode: code }),
                          )
                        ) {
                          codeMutation.mutate({ id: venue.id, code });
                        }
                      }}
                    >
                      <label className="form-control">
                        <span className="label-text">{t('venue.codeLabel')}</span>
                        <input
                          aria-label={t('venue.codeLabel')}
                          className="input input-sm input-bordered w-full sm:max-w-md"
                          value={codeDrafts[venue.id] ?? venue.code}
                          maxLength={32}
                          pattern={VENUE_CODE_PATTERN}
                          required
                          disabled={codeMutation.isPending}
                          onChange={(event) => {
                            setCodeDrafts((current) => ({
                              ...current,
                              [venue.id]: event.target.value,
                            }));
                            codeMutation.reset();
                          }}
                        />
                      </label>
                      <p className="text-xs text-base-content/70">{t('venue.codeFormat')}</p>
                      {codeMutation.isError && codeMutation.variables?.id === venue.id && (
                        <p role="alert" className="text-sm text-error">
                          {extractBusinessErrorMessage(
                            codeMutation.error,
                            t('venue.codeSaveFailed'),
                          )}
                        </p>
                      )}
                      {codeMutation.isSuccess && codeMutation.variables?.id === venue.id && (
                        <p role="status" className="text-sm text-success">
                          {t('venue.codeSaved')}
                        </p>
                      )}
                      <button
                        className="btn btn-sm btn-primary"
                        type="submit"
                        disabled={
                          codeMutation.isPending ||
                          updateMutation.isPending ||
                          (codeDrafts[venue.id] ?? venue.code) === venue.code ||
                          !VENUE_CODE_REGEX.test(codeDrafts[venue.id] ?? venue.code)
                        }
                      >
                        {t('venue.saveCode')}
                      </button>
                      <button
                        className="btn btn-sm btn-ghost ml-2"
                        type="button"
                        disabled={codeMutation.isPending}
                        onClick={() => {
                          setCodeDrafts((current) => ({ ...current, [venue.id]: venue.code }));
                          codeMutation.reset();
                        }}
                      >
                        {t('venue.cancelCode')}
                      </button>
                    </form>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                      <div className="join w-full sm:max-w-md sm:flex-1">
                        <input
                          value={renameDrafts[venue.id] ?? venue.name}
                          onChange={(event) =>
                            setRenameDrafts((current) => ({
                              ...current,
                              [venue.id]: event.target.value,
                            }))
                          }
                          aria-label={t('venue.renameLabel')}
                          className="input input-xs input-bordered join-item flex-1 min-w-0"
                        />
                        <button
                          className="btn btn-xs btn-primary join-item"
                          disabled={
                            updateMutation.isPending ||
                            !(renameDrafts[venue.id] ?? venue.name).trim()
                          }
                          onClick={() =>
                            updateMutation.mutate({
                              id: venue.id,
                              data: { name: (renameDrafts[venue.id] ?? venue.name).trim() },
                            })
                          }
                        >
                          {t('venue.saveName')}
                        </button>
                      </div>
                      <select
                        defaultValue={venue.edition}
                        onChange={(e) =>
                          updateMutation.mutate({
                            id: venue.id,
                            data: { edition: e.target.value as UpdateVenueRequest['edition'] },
                          })
                        }
                        className="select select-xs select-bordered"
                      >
                        <option value="LITE">{t('venue.editionLite')}</option>
                        <option value="BUSINESS">{t('venue.editionBusiness')}</option>
                        <option value="ENTERPRISE">{t('venue.editionEnterprise')}</option>
                      </select>
                      {venue.isActive ? (
                        <button
                          onClick={() =>
                            updateMutation.mutate({ id: venue.id, data: { isActive: false } })
                          }
                          disabled={updateMutation.isPending}
                          className="btn btn-error btn-xs btn-outline"
                        >
                          {t('venue.deactivate')}
                        </button>
                      ) : (
                        <button
                          onClick={() =>
                            updateMutation.mutate({ id: venue.id, data: { isActive: true } })
                          }
                          disabled={updateMutation.isPending}
                          className="btn btn-success btn-xs btn-outline"
                        >
                          {t('venue.activate')}
                        </button>
                      )}
                    </div>
                    {updateMutation.isError && (
                      <p className="text-error text-sm mt-1">
                        {(updateMutation.error as Error)?.message ?? t('venue.updateFailed')}
                      </p>
                    )}
                    <div className="text-xs text-base-content/30 mt-2">
                      ID: {venue.id} | Created: {new Date(venue.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const renderMembersTab = () => (
    <>
      <div className="mb-4">
        <h2 className="text-lg font-bold mb-2">{t('venue.crossVenueMemberSearch')}</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSearchQuery(searchInput);
          }}
          className="flex gap-2"
        >
          <input
            type="text"
            placeholder={t('venue.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="input input-sm input-bordered flex-1"
          />
          <button
            type="submit"
            disabled={searchInput.length < 2 || isSearching}
            className="btn btn-primary btn-sm"
          >
            {isSearching ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              t('venue.search')
            )}
          </button>
        </form>
        <div className="mt-3 rounded-lg border border-base-300 bg-base-200 p-3 space-y-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={targetVenueId}
              onChange={(event) => setTargetVenueId(event.target.value)}
              className="select select-sm select-bordered flex-1"
            >
              <option value="">{t('venue.selectEnrollmentVenue')}</option>
              {venues
                .filter((venue) => venue.isActive)
                .map((venue) => (
                  <option key={venue.id} value={venue.id}>
                    {venue.name}
                  </option>
                ))}
            </select>
            <button
              className="btn btn-sm btn-primary"
              disabled={
                !targetVenueId || selectedMemberIds.length === 0 || enrollmentMutation.isPending
              }
              onClick={() => enrollmentMutation.mutate()}
            >
              {t('venue.enrollSelected', { count: selectedMemberIds.length })}
            </button>
          </div>
          <p className="text-xs text-warning">{t('venue.invalidBatchWarning')}</p>
          <p className="text-xs text-base-content/60">{t('venue.noBalanceCopy')}</p>
          {enrollmentMutation.isError && (
            <p className="text-sm text-error">{(enrollmentMutation.error as Error).message}</p>
          )}
        </div>
      </div>

      {enrollmentOutcomes.length > 0 &&
        (() => {
          const grouped = groupEnrollmentOutcomes(enrollmentOutcomes);
          return (
            <div className="mb-4 rounded-lg border border-base-300 p-3 text-sm">
              <p className="font-semibold">{t('venue.enrollmentResults')}</p>
              <p className="text-success">
                {t('venue.enrolledCount', { count: grouped.enrolled.length })}
              </p>
              <p>{t('venue.skippedCount', { count: grouped.skipped.length })}</p>
              <p className="text-error">
                {t('venue.invalidCount', { count: grouped.invalid.length })}
              </p>
              <ul className="mt-2 space-y-1 text-xs">
                {enrollmentOutcomes.map((outcome) => (
                  <li key={outcome.memberId}>
                    {outcome.memberId}: {t(`venue.outcome.${outcome.state}`)}
                    {outcome.reason ? ` — ${outcome.reason}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}

      {searchResults.length > 0 && (
        <div className="space-y-3">
          {searchResults.map((member) => (
            <div key={member.id} className="card bg-base-200">
              <div className="card-body p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm mt-1"
                      checked={selectedMemberIds.includes(member.id)}
                      onChange={(event) =>
                        setSelectedMemberIds((current) =>
                          event.target.checked
                            ? normalizeSelection([...current, member.id])
                            : current.filter((id) => id !== member.id),
                        )
                      }
                      aria-label={t('venue.selectMember', { name: member.nickname })}
                    />
                    <div>
                      <h3 className="font-semibold">{member.nickname}</h3>
                      <div className="text-sm text-base-content/60">
                        {member.phone && <span className="mr-3">{member.phone}</span>}
                        {member.email && <span>{member.email}</span>}
                      </div>
                    </div>
                  </div>
                  <span className="badge badge-sm badge-outline">
                    {member.memberVenues.length} {t('venue.venuesLabel')}
                  </span>
                </div>
                <div className="mt-2 space-y-1">
                  {member.memberVenues.map((mv) => (
                    <div
                      key={mv.venueId}
                      className="flex items-center justify-between text-sm bg-base-300/50 rounded px-3 py-1.5"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{mv.venue.name}</span>
                        <span className="text-xs text-base-content/50">{mv.memberNo}</span>
                        {mv.level && (
                          <span
                            className="badge badge-xs"
                            style={{ backgroundColor: mv.level.color, color: '#fff' }}
                          >
                            {mv.level.displayName}
                          </span>
                        )}
                        <span
                          className={`badge badge-xs ${
                            mv.status === 'ACTIVE'
                              ? 'badge-success'
                              : mv.status === 'BLACKLISTED'
                                ? 'badge-error'
                                : 'badge-ghost'
                          }`}
                        >
                          {mv.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && (
        <p className="text-center text-base-content/50 py-8">{t('venue.noResults')}</p>
      )}
    </>
  );

  const renderReportsTab = () => (
    <>
      <div className="mb-4">
        <h2 className="text-lg font-bold mb-2">{t('venue.tournamentReport')}</h2>
        <div className="flex gap-2 items-end">
          <div>
            <label className="label label-text text-xs">{t('venue.from')}</label>
            <input
              type="date"
              value={reportFrom}
              onChange={(e) => setReportFrom(e.target.value)}
              className="input input-sm input-bordered"
            />
          </div>
          <div>
            <label className="label label-text text-xs">{t('venue.to')}</label>
            <input
              type="date"
              value={reportTo}
              onChange={(e) => setReportTo(e.target.value)}
              className="input input-sm input-bordered"
            />
          </div>
          {isReportLoading && <span className="loading loading-spinner loading-sm" />}
        </div>
      </div>

      {report && (
        <>
          {/* Grand totals */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { val: report.grandTotal.tournamentCount, label: t('venue.totalTournaments') },
              {
                val: `$${report.grandTotal.totalPrizePool.toLocaleString()}`,
                label: t('venue.totalPrizePool'),
              },
              { val: report.grandTotal.totalPlayers, label: t('venue.totalPlayers') },
            ].map(({ val, label }) => (
              <div key={label} className="card bg-base-200">
                <div className="card-body p-4 text-center">
                  <div className="text-2xl font-bold">{val}</div>
                  <div className="text-xs text-base-content/50">{label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Per-venue breakdown */}
          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t('venue.venueName')}</th>
                  <th className="text-right">{t('venue.tournamentCount')}</th>
                  <th className="text-right">{t('venue.prizePool')}</th>
                  <th className="text-right">{t('venue.players')}</th>
                  <th className="text-right">{t('venue.avgBuyin')}</th>
                </tr>
              </thead>
              <tbody>
                {report.venues.map((v) => (
                  <tr key={v.venueId}>
                    <td>
                      <span className="font-medium">{v.venueName}</span>
                      <span className="text-xs text-base-content/50 ml-2">{v.venueCode}</span>
                    </td>
                    <td className="text-right">{v.tournamentCount}</td>
                    <td className="text-right">${v.totalPrizePool.toLocaleString()}</td>
                    <td className="text-right">{v.totalPlayers}</td>
                    <td className="text-right">${v.avgBuyin.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Tabs */}
      <div role="tablist" className="tabs tabs-bordered mb-6 gap-0 [&>.tab]:px-6">
        <button
          role="tab"
          className={`tab ${tab === 'venues' ? 'tab-active' : ''}`}
          onClick={() => setTab('venues')}
        >
          {t('venue.tabVenues')}
        </button>
        <button
          role="tab"
          className={`tab ${tab === 'members' ? 'tab-active' : ''}`}
          onClick={() => setTab('members')}
        >
          {t('venue.tabMembers')}
        </button>
        <button
          role="tab"
          className={`tab ${tab === 'reports' ? 'tab-active' : ''}`}
          onClick={() => setTab('reports')}
        >
          {t('venue.tabReports')}
        </button>
      </div>

      {tab === 'venues' && renderVenuesTab()}
      {tab === 'members' && renderMembersTab()}
      {tab === 'reports' && renderReportsTab()}
    </div>
  );
}
